// Excel-Paste-Parser fuer die WE-Anlage (Spec §4): TSV rein, Spalten-Zuordnung drauf,
// deutsche Zahlformate tolerant parsen. Pure — vitest-getestet.
export interface PasteGrid { rows: string[][]; colCount: number }
export interface ColumnMapping { artikel: number; menge: number; kartons: number }
export const MAPPING_STORAGE_KEY = 'we-paste-mapping-v1'

export function parsePaste(raw: string): PasteGrid {
  const rows = raw.split(/\r?\n/).map((l) => l.split('\t').map((c) => c.trim()))
    .filter((cells) => cells.some((c) => c.length > 0))
  const colCount = rows.reduce((m, r) => Math.max(m, r.length), 0)
  return { rows, colCount }
}

function parseGermanInt(s: string): number | null {
  const cleaned = s.replace(/\./g, '').replace(',', '.').trim()
  if (!/^\d+(\.0+)?$/.test(cleaned)) return null
  const n = parseInt(cleaned, 10)
  return Number.isFinite(n) && n > 0 ? n : null
}

export function looksLikeHeader(row: string[]): boolean {
  return row.length > 0 && row.every((c) => parseGermanInt(c) === null)
}

export function applyMapping(grid: PasteGrid, m: ColumnMapping):
  Array<{ artikel: string; totalQty: number | null; cartons: number | null; raw: string[] }> {
  return grid.rows.map((r) => ({
    artikel: (r[m.artikel] ?? '').trim(),
    totalQty: parseGermanInt(r[m.menge] ?? ''),
    cartons: parseGermanInt(r[m.kartons] ?? ''),
    raw: r,
  }))
}

// L4-Fix (Codex Gate B): ein aus localStorage geladenes Mapping kann Spalten-Indizes
// enthalten, die zu einem NEUEN Paste (anderer colCount) nicht mehr passen (z.B.
// gespeichert gegen eine 5-spaltige Tabelle, jetzt nur 2 Spalten eingefuegt) — ein
// out-of-range-Index liefert dann `undefined` in applyMapping und die Zeile wird
// stillschweigend falsch/leer gemappt. Jeder Index wird gegen `colCount` geprueft;
// ist er ungueltig, greift der Default (0/1/2) NUR wenn der selbst in den Grid
// passt ("soweit vorhanden"), sonst faellt der Wert auf die letzte verfuegbare
// Spalte zurueck (min. 1 Spalte ist bei einem nicht-leeren Grid immer vorhanden).
export function validateMapping(mapping: ColumnMapping, colCount: number): ColumnMapping {
  const DEFAULTS: ColumnMapping = { artikel: 0, menge: 1, kartons: 2 }
  const clamp = (idx: number, def: number): number => {
    if (Number.isInteger(idx) && idx >= 0 && idx < colCount) return idx
    if (def < colCount) return def
    return Math.max(0, colCount - 1)
  }
  return {
    artikel: clamp(mapping.artikel, DEFAULTS.artikel),
    menge: clamp(mapping.menge, DEFAULTS.menge),
    kartons: clamp(mapping.kartons, DEFAULTS.kartons),
  }
}

// M3-Fix (Codex Gate B): Menge/Kartons einer WE-Anlage-Zeile muessen positive
// Ganzzahlen sein (>= 1) — sowohl fuer manuell getippte Werte (Number-Input erlaubt
// technisch 0/negativ/Dezimalzahlen trotz min/step) als auch als letzte
// Verteidigungslinie fuer den Paste-Pfad.
export function isPositiveInteger(v: number): boolean {
  return Number.isInteger(v) && v > 0
}
